import React, { useState } from "react";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useLang } from "@/i18n";

const SYNTAX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function EmailField({ value, onChange, className = "", testid = "email-input", onStatus, required = false, invalid }) {
  const { t } = useLang();
  const [state, setState] = useState(null); // null | checking | ok | nodomain | invalid
  const [reason, setReason] = useState("");

  const syntaxOk = !value || SYNTAX.test(value);

  const check = async () => {
    if (!value) { setState(null); onStatus && onStatus(null); return; }
    if (!SYNTAX.test(value)) { setState("invalid"); setReason(t("email_invalid")); onStatus && onStatus("invalid"); return; }
    setState("checking");
    try {
      const { data } = await api.get(`/validate-email?email=${encodeURIComponent(value)}`);
      const st = !data.syntax ? "invalid" : data.deliverable ? "ok" : "nodomain";
      setState(st);
      setReason(st === "invalid" ? t("email_invalid") : st === "nodomain" ? t("email_no_domain") : "");
      onStatus && onStatus(st);
    } catch { setState(null); }
  };

  const bad = !!value && (!syntaxOk || state === "invalid" || state === "nodomain");
  const good = !!value && syntaxOk && state !== "invalid" && state !== "nodomain" && state !== "checking";
  const icon = state === "checking" ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
    : bad ? <XCircle className="h-5 w-5 text-rose-500" />
    : good ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : null;

  return (
    <div>
      <div className="relative">
        <Input type="email" required={required} data-invalid={invalid} value={value} onChange={(e) => { onChange(e); if (state) setState(null); }} onBlur={check}
          className={`pr-9 ${bad ? "border-rose-400" : good ? "border-emerald-500" : ""} ${className}`}
          data-testid={testid} autoComplete="off" />
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2" data-testid={`${testid}-status`} data-state={bad ? "invalid" : good ? "ok" : state || ""}>{icon}</span>
      </div>
      {(!syntaxOk || state === "invalid" || state === "nodomain") && (
        <p className="text-[11px] mt-1 text-rose-600" data-testid={`${testid}-message`}>
          {!syntaxOk && state !== "nodomain" ? t("email_invalid") : reason}
        </p>
      )}
    </div>
  );
}
